/**
 * New goal (Goals UX pass 2026-09-06). A title is enough; the rest sits under
 * two named groups — "Helps me plan the journey" (what it belongs to or which
 * parts of life, by when, priority) and "Helps me keep you going" (why it
 * matters, what done means). The form offers what the goal page's Add row
 * offers, and nothing else; the journey itself is planned on the goal.
 */
import React, { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Target } from 'lucide-react'
import { HierarchyCreationForm, type BasicField, type FieldGroup } from '../components/hierarchy/HierarchyCreationForm'
import { GoalService } from '../services/goalService'
import { useToast } from '../components/ui/Toast'
import { useLifeAreaOptions } from '../hooks/useLifeAreas'
import { LIFE_AREA_OPTIONS, lifeAreaLabel, lifeAreaValue } from '../utils/taskForm'
import { GOAL_PRIORITY_OPTIONS } from '../utils/goalOptions'
import { goalFormToPayload, type GoalFormData } from '../utils/goalForm'

const PLAN_GROUP: FieldGroup = { key: 'plan', title: 'Helps me plan the journey', blurb: 'What it is for and by when. Skip what you do not know yet.' }
const KEEP_GROUP: FieldGroup = { key: 'keep', title: 'Helps me keep you going', blurb: 'Why it matters and what done will look like, in your own words.' }

const FIELDS: BasicField[] = [
  { name: 'title', label: 'What is the goal?', type: 'text', required: true, placeholder: 'e.g., Run a 10k · A senior role · Debt-free by spring', section: 'basic' },
  { name: 'parent_link', span: 'half', label: 'Belongs to', type: 'parent_picker', allowedParentTypes: ['dream'], group: PLAN_GROUP, section: 'basic' },
  { name: 'target_date', span: 'half', label: 'Get there by', type: 'date', group: PLAN_GROUP, section: 'basic', helpText: 'A rough date is fine. When it passes, I will ask how it is going.' },
  {
    name: 'life_areas', label: 'Which parts of life is it for?', type: 'chips', multi: true, options: LIFE_AREA_OPTIONS,
    allowCustom: true, customLabel: 'Another…', customPlaceholder: 'e.g. Side project', customValue: lifeAreaValue, labelOf: lifeAreaLabel,
    helpText: 'Pick one or more; the first is the main one. Name your own if none of these fits.',
    group: PLAN_GROUP, section: 'basic',
  },
  { name: 'priority', label: 'Priority', type: 'chips', options: GOAL_PRIORITY_OPTIONS, group: PLAN_GROUP, section: 'basic' },
  { name: 'description', label: 'Why it matters', type: 'text', placeholder: 'What reaching this would change — one line is plenty', group: KEEP_GROUP, section: 'basic' },
  { name: 'success_criteria', label: 'Done means', type: 'text', placeholder: 'What will be true when this is done — the old jeans fit, the offer is signed', group: KEEP_GROUP, section: 'basic' },
]

export const CreateGoal: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const seededTitle = (location.state as { title?: string } | null)?.title || ''
  const [formData, setFormData] = useState<GoalFormData>({ title: seededTitle, priority: 'medium', parent_type: '', parent_id: null })
  const [formError, setFormError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const goalService = useMemo(() => new GoalService(), [])

  const picked: string[] = Array.isArray(formData.life_areas) ? formData.life_areas.map(String) : []
  const { options: lifeAreaOptions, remember: rememberLifeArea } = useLifeAreaOptions(picked)
  const fields = useMemo(() => FIELDS.map((f) => (f.name === 'life_areas' ? { ...f, options: lifeAreaOptions } : f)), [lifeAreaOptions])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)
    if (!String(formData.title || '').trim()) {
      setFormError('Give it a name — that is the one thing I need.')
      return
    }
    setIsLoading(true)
    try {
      const created = await goalService.createGoal(goalFormToPayload(formData))
      picked.forEach(rememberLifeArea)
      toast.success('Goal added', created.title)
      navigate(created?.id ? `/goals/${created.id}` : '/goals')
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not add the goal')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <HierarchyCreationForm
      title="New goal"
      description="A name is enough. What it is for, by when and the journey there can be added on the goal itself, whenever you know."
      icon={<Target className="h-8 w-8 text-brand" />}
      basicFields={fields}
      advancedFields={[]}
      compactHeader
      formData={formData}
      onFormDataChange={setFormData}
      onSubmit={handleSubmit}
      submitLabel="Save"
      error={formError}
      primaryColor="indigo"
      backUrl="/goals"
      isLoading={isLoading}
    />
  )
}

export default CreateGoal
